import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { apiGet, apiPost } from "../../services/api/apiClient";

interface DropboxFoldersState {
  folders: unknown[];
  selectedFolder: unknown | null;
  thumbnails: Record<string, string>;
  singleThumbnails: Record<string, string>;
  previews: Record<string, string>;
  downloads: Record<string, string>;
  temporaryLinks: Record<string, string>;
  sharedLinks: Record<string, string>;
  loading: boolean;
  folderLoading: boolean;
  thumbnailsLoading: boolean;
  previewLoading: boolean;
  downloadLoading: Record<string, boolean>;
  temporaryLinkLoading: boolean;
  sharedLinkLoading: boolean;
  error: string | null;
  success: boolean;
  lastSyncTime: string | null;
  searchResults: unknown[];
  searchLoading: boolean;
  searchError: string | null;
  searchTotalCount: number;
  searchPage: number;
}

// Initial state
const initialState: DropboxFoldersState = {
  folders: [],
  selectedFolder: null,
  thumbnails: {},
  singleThumbnails: {},
  previews: {},
  downloads: {},
  temporaryLinks: {},
  sharedLinks: {},
  loading: false,
  folderLoading: false,
  thumbnailsLoading: false,
  previewLoading: false,
  downloadLoading: {}, // Changed to object to track individual file download states
  temporaryLinkLoading: false,
  sharedLinkLoading: false,
  error: null,
  success: false,
  lastSyncTime: null,
  // Deep search state
  searchResults: [],
  searchLoading: false,
  searchError: null,
  searchTotalCount: 0,
  searchPage: 1,
};

// Async thunk for getting dropbox folders
export const getDropboxFolders = createAsyncThunk(
  "dropboxFolders/getDropboxFolders",
  async (_, { rejectWithValue, getState, signal }) => {
    try {
      const { login } = getState() as any;
      const user = login.user;

      const result = await apiGet({
        endpoint: "/get-dropbox-folders",
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
        signal,
      });
      return result;
    } catch (error: any) {
      if (error.name === "AbortError") {
        return rejectWithValue("Request cancelled");
      }
      return rejectWithValue(error.message);
    }
  },
);

// Async thunk for getting a specific dropbox folder by ID
export const getDropboxFolder = createAsyncThunk(
  "dropboxFolders/getDropboxFolder",
  async (folderId, { rejectWithValue, getState, signal }) => {
    try {
      const { login } = getState() as any;
      const user = login.user;

      const result = await apiGet({
        endpoint: `/get-dropbox-folder?id=${folderId}`,
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
        signal,
      });
      return result;
    } catch (error: any) {
      if (error.name === "AbortError") {
        return rejectWithValue("Request cancelled");
      }
      return rejectWithValue(error.message);
    }
  },
);

// Async thunk for batch getting dropbox thumbnails
export const batchGetDropboxThumbnails = createAsyncThunk(
  "dropboxFolders/batchGetDropboxThumbnails",
  async (filePaths, { rejectWithValue, getState, signal }) => {
    try {
      const { login } = getState() as any;
      const user = login.user;

      const result = await apiPost({
        endpoint: "/batch-get-dropbox-thumbnails",
        data: { filePaths },
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
        signal,
      });
      return result;
    } catch (error: any) {
      if (error.name === "AbortError") {
        return rejectWithValue("Request cancelled");
      }
      return rejectWithValue(error.message);
    }
  },
);

// Async thunk for getting a single dropbox thumbnail with size specification
export const getDropboxThumbnail = createAsyncThunk(
  "dropboxFolders/getDropboxThumbnail",
  async ({ filePath }: any, { rejectWithValue, getState, signal }) => {
    try {
      const { login } = getState() as any;
      const user = login.user;

      const result = await apiPost({
        endpoint: "/get-dropbox-thumbnail",
        data: { filePath },
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
        signal,
      });
      return result;
    } catch (error: any) {
      if (error.name === "AbortError") {
        return rejectWithValue("Request cancelled");
      }
      return rejectWithValue(error.message);
    }
  },
);

// Async thunk for getting dropbox file preview
export const getDropboxPreview = createAsyncThunk(
  "dropboxFolders/getDropboxPreview",
  async ({ filePath }: any, { rejectWithValue, getState, signal }) => {
    try {
      const { login } = getState() as any;
      const user = login.user;

      const result = await apiPost({
        endpoint: "/get-dropbox-preview",
        data: { filePath },
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
        signal,
      });
      return { filePath, ...result };
    } catch (error: any) {
      if (error.name === "AbortError") {
        return rejectWithValue("Request cancelled");
      }
      return rejectWithValue(error.message);
    }
  },
);

// Async thunk for getting dropbox file download
export const getDropboxDownload = createAsyncThunk(
  "dropboxFolders/getDropboxDownload",
  async ({ filePath }: any, { rejectWithValue, getState, signal }) => {
    try {
      const { login } = getState() as any;
      const user = login.user;

      const result = await apiPost({
        endpoint: "/get-dropbox-download",
        data: { filePath },
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
        signal,
      });
      return { filePath, ...result };
    } catch (error: any) {
      if (error.name === "AbortError") {
        return rejectWithValue("Request cancelled");
      }
      return rejectWithValue(error.message);
    }
  },
);

// Async thunk for getting dropbox temporary link
export const getDropboxTemporaryLink = createAsyncThunk(
  "dropboxFolders/getDropboxTemporaryLink",
  async ({ filePath }: any, { rejectWithValue, getState, signal }) => {
    try {
      const { login } = getState() as any;
      const user = login.user;

      const result = await apiPost({
        endpoint: "/get-dropbox-temporary-link",
        data: { filePath },
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
        signal,
      });
      return { filePath, ...result };
    } catch (error: any) {
      if (error.name === "AbortError") {
        return rejectWithValue("Request cancelled");
      }
      return rejectWithValue(error.message);
    }
  },
);

// Async thunk for downloading dropbox files (separate from preview loading)
export const downloadDropboxFile = createAsyncThunk(
  "dropboxFolders/downloadDropboxFile",
  async ({ filePath }: any, { rejectWithValue, getState, signal }) => {
    try {
      const { login } = getState() as any;
      const user = login.user;

      const result = await apiPost({
        endpoint: "/get-dropbox-temporary-link",
        data: { filePath },
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
        signal,
      });
      return { filePath, ...result };
    } catch (error: any) {
      if (error.name === "AbortError") {
        return rejectWithValue("Request cancelled");
      }
      return rejectWithValue(error.message);
    }
  },
);

// Async thunk for updating dropbox folder settings
export const updateDropboxFolder = createAsyncThunk(
  "dropboxFolders/updateDropboxFolder",
  async (
    { folderId, projectsAssigned, displayName }: any,
    { rejectWithValue, getState, signal },
  ) => {
    try {
      const { login } = getState() as any;
      const user = login.user;

      const result = await apiPost({
        endpoint: "/update-dropbox-folder",
        data: { folderId, projectsAssigned, displayName },
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
        signal,
      });
      // The updated folder data is expected in result.data
      return result.data;
    } catch (error: any) {
      if (error.name === "AbortError") {
        return rejectWithValue("Request cancelled");
      }
      return rejectWithValue(error.message);
    }
  },
);

// Async thunk for deep searching dropbox files and folders
export const searchDropbox = createAsyncThunk(
  "dropboxFolders/searchDropbox",
  async (
    { query, type = "", page = 1, limit = 50, projectId = "" }: any,
    { rejectWithValue, getState, signal },
  ) => {
    try {
      const { login } = getState() as any;
      const user = login.user;

      const params = new URLSearchParams({
        q: query,
        page: String(page),
        limit: String(limit),
      });
      if (type) params.set("type", type);
      if (projectId) params.set("projectId", projectId);

      const result = await apiGet({
        endpoint: `/search-dropbox?${params.toString()}`,
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
        signal,
      });
      return result;
    } catch (error: any) {
      if (error.name === "AbortError") {
        return rejectWithValue("Request cancelled");
      }
      return rejectWithValue(error.message);
    }
  },
);

// Async thunk for creating dropbox shared link
export const createDropboxSharedLink = createAsyncThunk(
  "dropboxFolders/createDropboxSharedLink",
  async ({ path }: any, { rejectWithValue, getState, signal }) => {
    try {
      const { login } = getState() as any;
      const user = login.user;

      const result = await apiPost({
        endpoint: "/create-dropbox-shared-link",
        data: { path },
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
        signal,
      });
      return { path, ...result };
    } catch (error: any) {
      if (error.name === "AbortError") {
        return rejectWithValue("Request cancelled");
      }
      return rejectWithValue(error.message);
    }
  },
);

// Slice
const dropboxFoldersSlice = createSlice({
  name: "dropboxFolders",
  initialState,
  reducers: {
    resetState: () => {
      return initialState;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearSelectedFolder: (state) => {
      state.selectedFolder = null;
    },
    clearThumbnails: (state) => {
      state.thumbnails = {};
    },
    clearSingleThumbnails: (state) => {
      state.singleThumbnails = {};
    },
    clearPreviews: (state) => {
      state.previews = {};
    },
    clearDownloads: (state) => {
      state.downloads = {};
    },
    clearTemporaryLinks: (state) => {
      state.temporaryLinks = {};
    },
    clearSharedLinks: (state) => {
      state.sharedLinks = {};
    },
    clearSearchResults: (state) => {
      state.searchResults = [];
      state.searchTotalCount = 0;
      state.searchPage = 1;
      state.searchError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getDropboxFolders.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(getDropboxFolders.fulfilled, (state, action: any) => {
        state.loading = false;
        state.folders = action.payload?.data || [];
        state.lastSyncTime = action.payload?.lastSyncTime || null;
        state.success = true;
        state.error = null;
      })
      .addCase(getDropboxFolders.rejected, (state, action: any) => {
        state.loading = false;
        state.success = false;
        state.error = action.payload;
      })
      .addCase(getDropboxFolder.pending, (state) => {
        state.folderLoading = true;
        state.error = null;
      })
      .addCase(getDropboxFolder.fulfilled, (state, action: any) => {
        state.folderLoading = false;
        state.selectedFolder = action.payload?.data || null;
        state.error = null;
      })
      .addCase(getDropboxFolder.rejected, (state, action: any) => {
        state.folderLoading = false;
        state.error = action.payload;
      })
      .addCase(batchGetDropboxThumbnails.pending, (state) => {
        state.thumbnailsLoading = true;
        state.error = null;
      })
      .addCase(batchGetDropboxThumbnails.fulfilled, (state, action: any) => {
        state.thumbnailsLoading = false;
        state.thumbnails = action.payload?.thumbnails || {};
        state.error = null;
      })
      .addCase(batchGetDropboxThumbnails.rejected, (state, action: any) => {
        state.thumbnailsLoading = false;
        state.error = action.payload;
      })
      .addCase(getDropboxThumbnail.pending, (state) => {
        state.thumbnailsLoading = true;
        state.error = null;
      })
      .addCase(getDropboxThumbnail.fulfilled, (state, action: any) => {
        state.thumbnailsLoading = false;
        // Store the thumbnail in the singleThumbnails object with the file path as the key
        if (action.payload?.thumbnail && action.meta?.arg?.filePath) {
          state.singleThumbnails[action.meta.arg.filePath] =
            action.payload.thumbnail;
        }
        state.error = null;
      })
      .addCase(getDropboxThumbnail.rejected, (state, action: any) => {
        state.thumbnailsLoading = false;
        state.error = action.payload;
      })
      .addCase(getDropboxPreview.pending, (state) => {
        state.previewLoading = true;
        state.error = null;
      })
      .addCase(getDropboxPreview.fulfilled, (state, action: any) => {
        state.previewLoading = false;
        // Store the preview in the previews object with the file path as the key
        if (action.payload?.filePath) {
          state.previews[action.payload.filePath] = action.payload;
        }
        state.error = null;
      })
      .addCase(getDropboxPreview.rejected, (state, action: any) => {
        state.previewLoading = false;
        state.error = action.payload;
      })
      .addCase(getDropboxDownload.pending, (state, action: any) => {
        const filePath = action.meta.arg?.filePath;
        if (filePath) {
          state.downloadLoading[filePath] = true;
        }
        state.error = null;
      })
      .addCase(getDropboxDownload.fulfilled, (state, action: any) => {
        const filePath = action.meta.arg?.filePath;
        if (filePath) {
          state.downloadLoading[filePath] = false;
        }
        // Store the download in the downloads object with the file path as the key
        if (action.payload?.filePath) {
          state.downloads[action.payload.filePath] = action.payload;
        }
        state.error = null;
      })
      .addCase(getDropboxDownload.rejected, (state, action: any) => {
        const filePath = action.meta.arg?.filePath;
        if (filePath) {
          state.downloadLoading[filePath] = false;
        }
        state.error = action.payload;
      })
      .addCase(getDropboxTemporaryLink.pending, (state) => {
        state.temporaryLinkLoading = true;
        state.error = null;
      })
      .addCase(getDropboxTemporaryLink.fulfilled, (state, action: any) => {
        state.temporaryLinkLoading = false;
        // Store the temporary link in the temporaryLinks object with the file path as the key
        if (action.payload?.filePath) {
          state.temporaryLinks[action.payload.filePath] = action.payload?.link;
        }
        state.error = null;
      })
      .addCase(getDropboxTemporaryLink.rejected, (state, action: any) => {
        state.temporaryLinkLoading = false;
        state.error = action.payload;
      })
      .addCase(downloadDropboxFile.pending, (state, action: any) => {
        const filePath = action.meta.arg?.filePath;
        if (filePath) {
          state.downloadLoading[filePath] = true;
        }
        state.error = null;
      })
      .addCase(downloadDropboxFile.fulfilled, (state, action: any) => {
        const filePath = action.meta.arg?.filePath;
        if (filePath) {
          state.downloadLoading[filePath] = false;
        }
        // Store the download link in downloads object with the file path as the key
        if (action.payload?.filePath) {
          state.downloads[action.payload.filePath] = action.payload?.link;
        }
        state.error = null;
      })
      .addCase(downloadDropboxFile.rejected, (state, action: any) => {
        const filePath = action.meta.arg?.filePath;
        if (filePath) {
          state.downloadLoading[filePath] = false;
        }
        state.error = action.payload;
      })
      .addCase(createDropboxSharedLink.pending, (state) => {
        state.sharedLinkLoading = true;
        state.error = null;
      })
      .addCase(createDropboxSharedLink.fulfilled, (state, action: any) => {
        state.sharedLinkLoading = false;
        // Store the shared link in the sharedLinks object with the path as the key
        if (action.payload?.path) {
          state.sharedLinks[action.payload.path] = action.payload;
        }
        state.error = null;
      })
      .addCase(createDropboxSharedLink.rejected, (state, action: any) => {
        state.sharedLinkLoading = false;
        state.error = action.payload;
      })
      .addCase(searchDropbox.pending, (state) => {
        state.searchLoading = true;
        state.searchError = null;
      })
      .addCase(searchDropbox.fulfilled, (state, action: any) => {
        state.searchLoading = false;
        state.searchResults = action.payload?.data || [];
        state.searchTotalCount = action.payload?.totalCount || 0;
        state.searchPage = action.payload?.page || 1;
        state.searchError = null;
      })
      .addCase(searchDropbox.rejected, (state, action: any) => {
        state.searchLoading = false;
        state.searchError = action.payload;
      })
      .addCase(updateDropboxFolder.pending, (state) => {
        // Using general loading state, but a specific one could be added
        state.loading = true;
        state.error = null;
      })
      .addCase(updateDropboxFolder.fulfilled, (state, action: any) => {
        state.loading = false;
        const updatedFolder = action.payload;
        if (updatedFolder) {
          const index = state.folders.findIndex(
            (f: any) => f._id === updatedFolder._id,
          );
          if (index !== -1) {
            (state.folders as any)[index] = {
              ...(state.folders as any)[index],
              ...updatedFolder,
            };
          }
        }
      })
      .addCase(updateDropboxFolder.rejected, (state, action: any) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  resetState,
  clearError,
  clearSelectedFolder,
  clearThumbnails,
  clearSingleThumbnails,
  clearPreviews,
  clearDownloads,
  clearTemporaryLinks,
  clearSharedLinks,
  clearSearchResults,
} = dropboxFoldersSlice.actions;

export default dropboxFoldersSlice.reducer;
